import React, { useState } from 'react';
import { motion } from 'framer-motion';
import styles from './EnrollmentForm.module.css';
import { addDoc, enrollmentsRef } from '../../firebase';

interface FormData {
  name: string;
  qualification: string;
  email: string;
  phone: string;
  course: string;
}

const EnrollmentForm: React.FC<{ courseTitle?: string }> = ({ courseTitle }) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    qualification: '',
    email: '',
    phone: '',
    course: courseTitle || ''
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const qualifications = [
    "High School",
    "Diploma",
    "Bachelor's Degree",
    "Master's Degree",
    "PhD",
    "Other"
  ];

  const validate = (): boolean => {
    const newErrors: Partial<FormData> = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.qualification) newErrors.qualification = 'Qualification is required';
    if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) newErrors.email = 'Valid email is required';
    if (!formData.phone.match(/^[0-9]{10,15}$/)) newErrors.phone = 'Valid phone number is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  
  if (!validate()) return;
  
  setIsSubmitting(true);
  
  try {
    // Add document to Firestore
    const docRef = await addDoc(enrollmentsRef, {
      ...formData,
      createdAt: new Date().toISOString()
    });
    
    console.log("Document written with ID: ", docRef.id);
    setIsSuccess(true);
    setFormData({
      name: '',
      qualification: '',
      email: '',
      phone: '',
      course: courseTitle || ''
    });
  } catch (error) {
    console.error("Error adding document: ", error);
    alert('Submission failed. Please try again.');
  } finally {
    setIsSubmitting(false);
  }
};

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (isSuccess) {
    return (
      <motion.div 
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className={styles.successMessage}
      >
        <h3>🎉 Enrollment Successful!</h3>
        <p>We will sent course details to <strong>{formData.email}</strong></p>
        <button 
          onClick={() => setIsSuccess(false)}
          className={styles.successButton}
        >
          Close
        </button>
      </motion.div>
    );
  }

  return (
    <motion.form
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      onSubmit={handleSubmit}
      className={styles.formContainer}
    >
      <h3>Enroll Now</h3>
      
      <div className={styles.formGroup}>
        <label htmlFor="name">Full Name</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={errors.name ? styles.errorInput : ''}
        />
        {errors.name && <span className={styles.error}>{errors.name}</span>}
      </div>

      <div className={styles.formGroup}>
        <label htmlFor="qualification">Highest Qualification</label>
        <select
          id="qualification"
          name="qualification"
          value={formData.qualification}
          onChange={handleChange}
          className={errors.qualification ? styles.errorInput : ''}
        >
          <option value="">Select your qualification</option>
          {qualifications.map((q) => (
            <option key={q} value={q}>{q}</option>
          ))}
        </select>
        {errors.qualification && <span className={styles.error}>{errors.qualification}</span>}
      </div>

      <div className={styles.formGroup}>
        <label htmlFor="email">Email</label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={errors.email ? styles.errorInput : ''}
        />
        {errors.email && <span className={styles.error}>{errors.email}</span>}
      </div>

      <div className={styles.formGroup}>
        <label htmlFor="phone">Phone Number</label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          className={errors.phone ? styles.errorInput : ''}
        />
        {errors.phone && <span className={styles.error}>{errors.phone}</span>}
      </div>

      {!courseTitle && (
        <div className={styles.formGroup}>
          <label htmlFor="course">Course</label>
          <input
            type="text"
            id="course"
            name="course"
            value={formData.course}
            onChange={handleChange}
            required
          />
        </div>
      )}

      <motion.button
        type="submit"
        disabled={isSubmitting}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className={styles.submitButton}
      >
        {isSubmitting ? 'Submitting...' : 'Enroll Now'}
      </motion.button>
    </motion.form>
  );
};

export default EnrollmentForm;